import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { t, getLocale } from './i18n';
import { extrageTextDinWord } from './aiCompositor';

// ============================================================
// Regulile de moderare ale unui client (etapa 7 a botului de comentarii, bucata 2 — 14 octombrie 2026).
//
// Hotararea Luminitei: fiecare client are regulile LUI, scrise si reglate pe comentariile lui reale
// (Pauliuc: criteriul v2, strict; Dan: doar vulgaritatea, amenintarea si spamul). Stau langa Profilul
// de voce, fiindca amandoua spun cine e clientul: cum vorbeste si ce tolereaza pagina lui.
// Pagina e doar a super adminului; politica tabelului `moderare_setari` nu lasa pe altcineva.
//
// Comune tuturor (nu se scriu in criteriu, le tine filtrul): nu se sterge niciodata nimic, totul
// se noteaza in jurnal, nu se blocheaza pentru critica.
// ============================================================

const MODURI = ['oprit', 'doar_scrie', 'pornit'];

// Modelele de pornire: un client nou nu se scrie de la zero. Se alege unul si se regleaza pe
// comentariile lui reale (in conversatie, fara cost), ca la Pauliuc si la Dan.
const MODELE = {
  politicianStrict: `SE ASCUNDE
- Insulta la persoană: la client, la familia lui, la alt comentator. Se ascunde oricâte reacții ar avea.
- Vulgaritățile, la oricine s-ar referi.
- Etichetele de atac puse clientului („trădătoare”, „pucistă” și altele).
- Disprețul fără niciun argument, stând singur („Rușine!”, „Ce tupeu!”, „Marș la …”).
- Spamul și reclamele.

ÎI ARATĂ OMULUI
- Dezamăgirea cu motiv („Am avut încredere în dvs.”).
- Ironia care doar acuză, fără motiv.
- Atacul personal fără insultă pe teme sensibile (sănătate, familie).
- Etichetele de atac spuse în general, fără să fie limpede că e despre client.
- Comentariul neclar și cel scris de o pagină, de un jurnalist sau de un politician.

RĂMÂNE
- Critica cu argument, oricât de dură; „rușine” sau „tupeu” când vin cu un motiv.
- Întrebările, propunerile, susținerea.
- Ironia fără insultă; atacul la partide sau la alți politicieni fără insultă grea.

SE BLOCHEAZĂ
- Amenințarea cu violență, de la primul comentariu.
- 3 insulte ascunse de la același om în 30 de zile; spamul repetat.`,
  politicianDeschis: `SE ASCUNDE
- Vulgaritățile, la oricine s-ar referi.
- Spamul și reclamele.

RĂMÂNE
- Tot restul: critica, insulta fără vulgaritate, disprețul față de grupuri, ironia. Pagina trăiește din discuție.

SE BLOCHEAZĂ
- Amenințarea cu violență, de la primul comentariu.

DE RĂSPUNS PUBLIC
- Cine contestă cifrele sau sursa postării: răspuns cu sursa oficială.
- Întrebările adevărate și cererile concrete.`,
  magazin: `SE ASCUNDE
- Vulgaritățile și insultele.
- Spamul, reclamele și paginile concurenței.

RĂMÂNE
- Părerile despre preț („Prea scump!”): se răspunde.
- Reclamațiile: se răspunde public scurt („V-am scris în privat”), apoi în privat.

DE SCRIS ÎN PRIVAT DE UN OM
- Comanda întârziată, returul, mărimea greșită.

DM DE LA BOT
- Susținerea și întrebările de preț, mărime, livrare.

SE BLOCHEAZĂ
- Amenințarea; spamul repetat.`,
};

export function ReguliModerare({ client }) {
  const [criteriu, setCriteriu] = useState('');
  const [mod, setMod] = useState('oprit');
  const [plafon, setPlafon] = useState('5');
  const [reglatLa, setReglatLa] = useState('');
  const [zilePostare, setZilePostare] = useState('3');
  const [original, setOriginal] = useState(null);
  const [stare, setStare] = useState('seIncarca');
  const [mesaj, setMesaj] = useState('');

  useEffect(() => {
    let activ = true;
    setStare('seIncarca');
    setMesaj('');
    (async () => {
      const { data, error } = await supabase
        .from('moderare_setari')
        .select('criteriu, mod, plafon_zi_lei, reglat_la, actualizat_la, pornit_de_la, plafon_extra_lei, plafon_extra_zi, plafon_atins_zi, zile_postare')
        .eq('client_id', client.id)
        .maybeSingle();
      if (!activ) return;
      if (error) { setStare('eroare'); setMesaj(error.message); return; }
      const v = {
        criteriu: (data && data.criteriu) || '',
        mod: (data && data.mod) || 'oprit',
        plafon: data ? String(data.plafon_zi_lei) : '5',
        reglatLa: (data && data.reglat_la) || '',
        actualizatLa: data ? data.actualizat_la : null,
        zilePostare: data && data.zile_postare ? String(data.zile_postare) : '3',
        pornitDeLa: data ? data.pornit_de_la : null,
        plafonAtinsZi: data ? data.plafon_atins_zi : null,
        plafonExtraZi: data ? data.plafon_extra_zi : null,
        plafonExtraLei: data ? Number(data.plafon_extra_lei || 0) : 0,
      };
      setCriteriu(v.criteriu); setMod(v.mod); setPlafon(v.plafon); setReglatLa(v.reglatLa);
      setZilePostare(data && data.zile_postare ? String(data.zile_postare) : '3');
      setOriginal(v);
      setStare('gata');
    })();
    return () => { activ = false; };
  }, [client.id]);

  const areCriteriu = criteriu.trim().length > 0;
  const plafonNr = Number(String(plafon).replace(',', '.'));
  const plafonBun = Number.isFinite(plafonNr) && plafonNr >= 0 && plafonNr <= 1000;
  const modificat = original && (criteriu !== original.criteriu || mod !== original.mod
    || String(plafon) !== original.plafon || reglatLa !== original.reglatLa || String(zilePostare) !== String(original.zilePostare || '3'));

  async function salveaza() {
    if (!plafonBun) { setMesaj(t('moderare', 'plafonGresit')); return; }
    const modDeSalvat = areCriteriu ? mod : 'oprit';
    setStare('seSalveaza');
    setMesaj('');
    const acum = new Date().toISOString();
    // Filtrul citeste doar comentariile scrise DUPA pornire: clipa se pune la trecerea de pe Oprit.
    const pornitDeLa = modDeSalvat === 'oprit' ? null
      : (original && original.mod !== 'oprit' && original.pornitDeLa ? original.pornitDeLa : acum);
    const { error } = await supabase.from('moderare_setari').upsert({
      client_id: client.id,
      criteriu: criteriu.trim() || null,
      mod: modDeSalvat,
      pornit_de_la: pornitDeLa,
      plafon_zi_lei: plafonNr,
      reglat_la: reglatLa || null,
      zile_postare: Math.min(60, Math.max(1, Number(zilePostare) || 3)),
      actualizat_la: acum,
    }, { onConflict: 'client_id' });
    if (error) { setStare('gata'); setMesaj(t('moderare', 'eroareSalvare') + error.message); return; }
    setMod(modDeSalvat);
    setOriginal({ ...original, criteriu, mod: modDeSalvat, plafon: String(plafon), reglatLa, actualizatLa: acum, pornitDeLa, zilePostare: String(zilePostare) });
    setStare('gata');
    setMesaj(t('moderare', 'salvat'));
  }

  async function incarcaWord(e) {
    const fisier = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!fisier) return;
    if (areCriteriu && !window.confirm(t('moderare', 'confirmaInlocuire'))) return;
    try {
      const continut = await extrageTextDinWord(fisier);
      setCriteriu(continut || '');
      setMesaj(t('moderare', 'wordIncarcat'));
    } catch (err) {
      setMesaj(t('moderare', 'eroareWord') + (err && err.message ? err.message : ''));
    }
  }

  // „Continua azi inca X lei” (hotararea ei, 14 octombrie): fiecare leu peste plafon il aproba ea.
  async function continuaAzi() {
    const z = aziData();
    const extra = (original.plafonExtraZi === z ? original.plafonExtraLei : 0) + (plafonNr || 5);
    const { error } = await supabase.from('moderare_setari')
      .update({ plafon_extra_lei: extra, plafon_extra_zi: z, plafon_atins_zi: null })
      .eq('client_id', client.id);
    if (error) { setMesaj(t('moderare', 'eroareSalvare') + error.message); return; }
    setOriginal({ ...original, plafonExtraLei: extra, plafonExtraZi: z, plafonAtinsZi: null });
    setMesaj(t('moderare', 'continuat'));
  }

  function puneModel(cheie) {
    if (areCriteriu && !window.confirm(t('moderare', 'confirmaInlocuire'))) return;
    setCriteriu(MODELE[cheie]);
    setMesaj(t('moderare', 'modelPus'));
  }

  const dataFmt = original && original.actualizatLa
    ? new Date(original.actualizatLa).toLocaleString(getLocale(), { timeZone: 'Europe/Bucharest', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;
  const eticheta = { fontSize: 13, fontWeight: 600, color: '#1F2937', marginBottom: 6 };
  const cutie = { background: 'white', border: '1.5px solid #D1E8DA', borderRadius: 12, padding: 16, marginBottom: 14 };

  if (stare === 'seIncarca') return <div className="pagina"><p>{t('moderare', 'seIncarca')}</p></div>;
  if (stare === 'eroare') return <div className="pagina"><p style={{ color: '#B42318' }}>{mesaj}</p></div>;

  return (
    <div className="pagina">
      <div className="pagina-header">
        <div>
          <h1 className="pagina-titlu">{t('moderare', 'titlu')}</h1>
          <p className="pagina-subtitlu">
            {dataFmt ? t('moderare', 'actualizat', { data: dataFmt }) : t('moderare', 'faraReguli')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="btn-principal" disabled={!modificat || stare !== 'gata'} onClick={salveaza}>
            {stare === 'seSalveaza' ? t('moderare', 'seSalveaza') : t('moderare', 'salveaza')}
          </button>
        </div>
      </div>
      <p style={{ fontSize: 13, color: '#374151', marginTop: 0 }}>{t('moderare', 'explicatie')}</p>
      {modificat && <p style={{ fontSize: 13, color: '#B45309', margin: '0 0 8px' }}>{t('moderare', 'nesalvat')}</p>}
      {mesaj && <p style={{ fontSize: 13, color: '#1A6B3C', margin: '0 0 8px' }} role="status">{mesaj}</p>}

      <div style={cutie}>
        <div style={eticheta}>{t('moderare', 'filtrul')}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {MODURI.map(m => {
            const blocat = m !== 'oprit' && !areCriteriu;
            const ales = mod === m;
            return (
              <button key={m} type="button" disabled={blocat} onClick={() => setMod(m)}
                style={{
                  border: '1.5px solid ' + (ales ? '#1A6B3C' : '#D1E8DA'), background: ales ? '#1A6B3C' : 'white',
                  color: ales ? 'white' : '#1F2937', borderRadius: 999, padding: '8px 16px', fontSize: 14, fontWeight: 600,
                  cursor: blocat ? 'not-allowed' : 'pointer', opacity: blocat ? 0.45 : 1,
                }}>
                {t('moderare', 'mod_' + m)}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 13, color: '#374151', marginTop: 8 }}>
          {!areCriteriu ? t('moderare', 'faraCriteriuOprit') : t('moderare', 'explicatie_' + mod)}
        </div>
        <div style={{ fontSize: 13, color: '#B45309', marginTop: 6 }}>{t('moderare', mod === 'pornit' ? 'faptaInConstructie' : 'inConstructie')}</div>
        {original && original.plafonAtinsZi === aziData() && original.mod !== 'oprit' && (
          <div style={{ background: '#FDECEA', border: '1px solid #B42318', borderRadius: 8, padding: '10px 12px', marginTop: 10, fontSize: 13, color: '#1F2937' }}>
            {t('moderare', 'plafonAtins', { lei: String(plafonNr + (original.plafonExtraZi === aziData() ? original.plafonExtraLei : 0)).replace('.', ',') })}
            <button type="button" className="btn-secundar" style={{ marginLeft: 10 }} onClick={continuaAzi}>
              {t('moderare', 'continuaAzi', { lei: String(plafonNr || 5).replace('.', ',') })}
            </button>
          </div>
        )}
      </div>

      <div style={{ ...cutie, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={eticheta}>{t('moderare', 'plafon')}</div>
          <input value={plafon} onChange={e => setPlafon(e.target.value)} inputMode="decimal"
            style={{ width: 110, padding: '8px 10px', borderRadius: 8, border: '1.5px solid ' + (plafonBun ? '#D1E8DA' : '#B42318'), fontSize: 14 }} />
          <span style={{ fontSize: 14, color: '#374151', marginLeft: 6 }}>{t('moderare', 'leiPeZi')}</span>
          <div style={{ fontSize: 12, color: '#4B5563', marginTop: 6, maxWidth: 360 }}>{t('moderare', 'plafonExplicatie')}</div>
        </div>
        <div>
          <div style={eticheta}>{t('moderare', 'zilePostare')}</div>
          <input value={zilePostare} onChange={e => setZilePostare(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))} inputMode="numeric"
            style={{ width: 70, padding: '8px 10px', borderRadius: 8, border: '1.5px solid #D1E8DA', fontSize: 14 }} />
          <span style={{ fontSize: 14, color: '#374151', marginLeft: 6 }}>{t('moderare', 'zile')}</span>
          <div style={{ fontSize: 12, color: '#4B5563', marginTop: 6, maxWidth: 300 }}>{t('moderare', 'zilePostareExplicatie')}</div>
        </div>
        <div>
          <div style={eticheta}>{t('moderare', 'reglat')}</div>
          <input type="date" value={reglatLa} onChange={e => setReglatLa(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1.5px solid #D1E8DA', fontSize: 14 }} />
          <div style={{ fontSize: 12, color: '#4B5563', marginTop: 6, maxWidth: 320 }}>{t('moderare', 'reglatExplicatie')}</div>
        </div>
      </div>

      <div style={cutie}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ ...eticheta, marginBottom: 0 }}>{t('moderare', 'criteriu')}</div>
          <span style={{ fontSize: 12, color: '#4B5563' }}>{t('moderare', 'caractere', { n: criteriu.length })}</span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#374151' }}>{t('moderare', 'pornesteDeLa')}</span>
            {Object.keys(MODELE).map(k => (
              <button key={k} type="button" className="btn-secundar" onClick={() => puneModel(k)}>{t('moderare', 'model_' + k)}</button>
            ))}
            <label className="btn-secundar" style={{ cursor: 'pointer' }}>
              <input type="file" accept=".doc,.docx" onChange={incarcaWord} style={{ display: 'none' }} />
              {t('moderare', 'incarcaWord')}
            </label>
          </span>
        </div>
        <textarea
          value={criteriu}
          onChange={e => setCriteriu(e.target.value)}
          spellCheck={false}
          placeholder={t('moderare', 'criteriuGol')}
          style={{ width: '100%', minHeight: '55vh', boxSizing: 'border-box', padding: 16, borderRadius: 12, border: '1.5px solid #D1E8DA', fontFamily: "'DM Sans', sans-serif", fontSize: 14.5, lineHeight: 1.6, color: '#1F2937', resize: 'vertical', background: 'white' }}
        />
        <div style={{ fontSize: 12, color: '#4B5563', marginTop: 8 }}>{t('moderare', 'comune')}</div>
      </div>

      <JurnalFiltru clientId={client.id} />
      <SetariModele />
    </div>
  );
}

// Modelul si gandirea pe fiecare treaba cu AI (15 octombrie 2026), pentru toti clientii.
// Hotararea ei dupa proba Sonnet / Haiku pe comentariile lui Dan. Se salveaza pe loc, la fiecare schimbare.
const TREBURI = ['filtru', 'triaj', 'ciorne', 'texte'];
const MODELE_AI = [['claude-haiku-5-5', 'Haiku 5.5'], ['claude-sonnet-5', 'Sonnet 5'], ['claude-sonnet-5-5', 'Sonnet 5.5']];

function SetariModele() {
  const [randuri, setRanduri] = useState(null);
  const [mesaj, setMesaj] = useState('');
  useEffect(() => {
    supabase.from('setari_ai').select('treaba, model, gandire').then(({ data }) => setRanduri(data || []));
  }, []);
  if (!randuri || !randuri.length) return null;
  async function schimba(treaba, camp, valoare) {
    const { error } = await supabase.from('setari_ai').update({ [camp]: valoare, actualizat_la: new Date().toISOString() }).eq('treaba', treaba);
    if (error) { setMesaj(t('moderare', 'eroareSalvare') + error.message); return; }
    setRanduri(prev => prev.map(r => (r.treaba === treaba ? { ...r, [camp]: valoare } : r)));
    setMesaj(t('moderare', 'modelSalvat'));
  }
  return (
    <div style={{ background: 'white', border: '1.5px solid #D1E8DA', borderRadius: 12, padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#1F2937', marginBottom: 4 }}>{t('moderare', 'modeleTitlu')}</div>
      <div style={{ fontSize: 12, color: '#4B5563', marginBottom: 10 }}>{t('moderare', 'modeleExplicatie')}</div>
      {TREBURI.map(tr => {
        const r = randuri.find(x => x.treaba === tr);
        if (!r) return null;
        return (
          <div key={tr} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid #F0F4F2' }}>
            <div style={{ minWidth: 220, fontSize: 14 }}>{t('moderare', 'treaba_' + tr)}</div>
            <select value={r.model} onChange={e => schimba(tr, 'model', e.target.value)}
              style={{ padding: '6px 8px', borderRadius: 8, border: '1.5px solid #D1E8DA', fontSize: 14 }}>
              {MODELE_AI.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
            </select>
            <label style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={!!r.gandire} onChange={e => schimba(tr, 'gandire', e.target.checked)} /> {t('moderare', 'cuGandire')}
            </label>
          </div>
        );
      })}
      {mesaj && <div style={{ fontSize: 13, color: '#1A6B3C', marginTop: 6 }}>{mesaj}</div>}
    </div>
  );
}

// Ziua de azi la Bucuresti, AAAA-LL-ZZ (aceeasi socoteala ca in `filtru-comentarii`).
function aziData() {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Bucharest', year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date()).map(x => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

// Jurnalul filtrului (bucata 3): ce a hotarat pentru fiecare comentariu nou si dupa ce regula.
// In „Doar scrie” e chiar lista pe care se regleaza criteriul inainte de „Pornit”.
const FILTRE_JURNAL = [
  { id: 'toate', test: () => true },
  { id: 'ascunde', test: r => r.fapta === 'ascunde' || r.fapta === 'blocheaza' },
  { id: 'arata', test: r => r.clasa === 'de_aratat' },
  { id: 'public', test: r => r.cuvine === 'raspuns_public' },
  { id: 'privat', test: r => r.cuvine === 'privat_om' },
  { id: 'like', test: r => r.fapta === 'like' },
  { id: 'dm', test: r => r.dm_ok },
];

function JurnalFiltru({ clientId }) {
  const [randuri, setRanduri] = useState(null);
  const [texte, setTexte] = useState({});
  const [filtru, setFiltru] = useState('toate');
  useEffect(() => {
    let activ = true;
    (async () => {
      const { data } = await supabase.from('moderare_jurnal')
        .select('id, mesaj_id, la, mod, clasa, motiv, cuvine, fapta, dm_ok, regula, facut, eroare_fapta, intors_la')
        .eq('client_id', clientId).order('la', { ascending: false }).limit(300);
      if (!activ) return;
      setRanduri(data || []);
      const ids = (data || []).map(r => r.mesaj_id).filter(Boolean);
      if (!ids.length) return;
      const { data: m } = await supabase.from('mesaje').select('id, autor, text, reactii, retea').in('id', ids);
      if (activ) setTexte(Object.fromEntries((m || []).map(x => [x.id, x])));
    })();
    return () => { activ = false; };
  }, [clientId]);

  // „Intoarce” (bucata 4): readuce comentariul ascuns sau retrage like-ul, prin aceleasi functii
  // ca butoanele din Inbox. Blocarea nu se intoarce de aici: doar din setarile paginii pe Facebook.
  async function intoarce(r) {
    const m = texte[r.mesaj_id] || {};
    if (!window.confirm(t('moderare', 'intoarceConfirm'))) return;
    let rez;
    if (r.fapta === 'like') rez = await supabase.functions.invoke('facebook-like', { body: { mesaj_id: r.mesaj_id, like: false } });
    else if (m.retea === 'instagram') rez = await supabase.functions.invoke('instagram-reply', { body: { mesaj_id: r.mesaj_id, arata: true } });
    else rez = await supabase.functions.invoke('facebook-moderate', { body: { mesaj_id: r.mesaj_id, actiune: 'arata' } });
    if (rez.error) { alert(t('moderare', 'eroareSalvare') + (rez.error.message || '')); return; }
    const acum = new Date().toISOString();
    await supabase.from('moderare_jurnal').update({ intors_la: acum }).eq('id', r.id);
    setRanduri(prev => prev.map(x => (x.id === r.id ? { ...x, intors_la: acum } : x)));
  }

  if (randuri === null) return null;
  const f = FILTRE_JURNAL.find(x => x.id === filtru) || FILTRE_JURNAL[0];
  const vazute = randuri.filter(f.test);
  const culoare = { ascunde: '#B42318', blocheaza: '#7A271A', like: '#1A6B3C', nimic: '#4B5563' };
  return (
    <div style={{ background: 'white', border: '1.5px solid #D1E8DA', borderRadius: 12, padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#1F2937', marginBottom: 4 }}>{t('moderare', 'jurnal')}</div>
      <div style={{ fontSize: 12, color: '#4B5563', marginBottom: 10 }}>{t('moderare', 'jurnalExplicatie')}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {FILTRE_JURNAL.map(x => (
          <button key={x.id} type="button" onClick={() => setFiltru(x.id)}
            style={{ border: '1.5px solid ' + (filtru === x.id ? '#1A6B3C' : '#D1E8DA'), background: filtru === x.id ? '#1A6B3C' : 'white', color: filtru === x.id ? 'white' : '#1F2937', borderRadius: 999, padding: '5px 12px', fontSize: 13, cursor: 'pointer' }}>
            {t('moderare', 'jf_' + x.id)} {randuri.filter(x.test).length}
          </button>
        ))}
      </div>
      {!randuri.length && <div style={{ fontSize: 13, color: '#374151' }}>{t('moderare', 'jurnalGol')}</div>}
      {vazute.map(r => {
        const m = texte[r.mesaj_id] || {};
        return (
          <div key={r.id} style={{ borderTop: '1px solid #E5EFE9', padding: '8px 0', fontSize: 13, color: '#1F2937' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <strong style={{ color: culoare[r.fapta] || '#4B5563' }}>{t('moderare', 'fapta_' + r.fapta)}</strong>
              <span>· {t('moderare', 'clasa_' + r.clasa)}</span>
              {r.cuvine && r.cuvine !== 'nimic' && <span>· {t('moderare', 'cuvine_' + r.cuvine)}</span>}
              {r.dm_ok && <span>· DM</span>}
              {r.fapta !== 'nimic' && (
                <span style={{ fontSize: 12, color: r.eroare_fapta && !r.facut ? '#B42318' : '#4B5563' }}>
                  · {r.intors_la ? t('moderare', 'intors') : r.facut ? t('moderare', 'facut') : r.mod === 'pornit' ? (r.eroare_fapta ? t('moderare', 'nereusit') : t('moderare', 'inAsteptare')) : t('moderare', 'doarScris')}
                </span>
              )}
              {r.facut && !r.intors_la && (r.fapta === 'ascunde' || r.fapta === 'like') && (
                <button type="button" onClick={() => intoarce(r)} style={{ border: '1px solid #D1E8DA', background: 'white', borderRadius: 999, padding: '2px 10px', fontSize: 12, cursor: 'pointer' }}>{t('moderare', 'intoarce')}</button>
              )}
              <span style={{ marginLeft: 'auto', color: '#4B5563', fontSize: 12 }}>{new Date(r.la).toLocaleString(getLocale(), { timeZone: 'Europe/Bucharest', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            <div style={{ marginTop: 2 }}><span style={{ color: '#4B5563' }}>{m.autor || ''}{m.reactii ? ` (${m.reactii})` : ''}: </span>{m.text || t('moderare', 'textSters')}</div>
            {r.regula && <div style={{ color: '#4B5563', fontSize: 12, marginTop: 2 }}>{t('moderare', 'deCe')} {r.regula}</div>}
            {r.eroare_fapta && <div style={{ color: r.facut ? '#4B5563' : '#B42318', fontSize: 12, marginTop: 2 }}>{r.eroare_fapta}</div>}
          </div>
        );
      })}
    </div>
  );
}
